import {
  Megaphone,
  Clock,
  Palette,
  FileQuestion,
  Frown,
  AlertTriangle,
} from 'lucide-react'
import { RolePageData } from '../types'

export const marketingData: RolePageData = {
  id: 'marketing',
  slug: 'marketing',
  label: 'Marketing & Brand',
  icon: Megaphone,
  title: 'Create merch that reinforces the brand',
  description:
    'Launch campaigns and events with quality branded merchandise that actually impresses.',

  context:
    'You own brand perception. Merch is used for events, campaigns, and gifts — high visibility, high pressure.',

  jobToBeDone:
    "Create merch that reinforces the brand and doesn't embarrass us.",

  dailyReality: [
    'Constant deadlines and last-minute changes',
    'Stakeholders with strong opinions',
    'Too many decisions to make',
    'Quality expectations are high',
  ],

  painPoints: [
    {
      title: 'Boring or generic merch',
      description:
        "Standard catalog items that look like everyone else's. Your brand deserves better.",
      icon: Frown,
    },
    {
      title: 'Unclear print tradeoffs',
      description:
        "Embroidery vs screen print? DTG vs sublimation? It's hard to know what works best for your design.",
      icon: FileQuestion,
    },
    {
      title: 'Slow quotes and iterations',
      description:
        'Waiting days for pricing or mockups kills momentum when you need to move fast.',
      icon: Clock,
    },
    {
      title: 'Last-minute event stress',
      description:
        'Events have hard deadlines. Late delivery or quality issues are not an option.',
      icon: AlertTriangle,
    },
  ],

  hiddenFears: [
    'This will look cheap',
    "This doesn't match the brand",
    "I'll have to redo this",
  ],

  esgAngle: {
    level: 'medium',
    description:
      'Medium to high sensitivity, especially for external-facing merch. Sustainability is part of the brand signal.',
  },

  whatTheyValue: ['Taste', 'Creative confidence', 'Speed', 'Flexibility'],

  languageTheyUse: [
    'Campaigns',
    'Brand consistency',
    'Activation',
    'Events',
    'Giveaways',
    'Impressions',
  ],

  convincers: [
    {
      quote: 'Curated, not catalog',
      description:
        "We recommend products that match your brand, not just what's cheapest or most popular.",
    },
    {
      quote: 'Design previews in hours',
      description:
        'See realistic mockups with your logo before committing. Iterate fast.',
    },
    {
      quote: 'Recommended print methods',
      description:
        'We tell you exactly which technique works best for your design and material.',
    },
    {
      quote: 'Fast iterations when plans change',
      description:
        'Last-minute changes? We adapt quickly without starting from scratch.',
    },
  ],

  dealBreakers: [
    {
      title: 'Overwhelming catalog',
      description:
        "Endless options with no guidance wastes your time and leads to bad choices.",
    },
    {
      title: 'No creative guidance',
      description:
        'You need a partner who understands brand, not just a vendor who takes orders.',
    },
    {
      title: 'Slow back-and-forth',
      description:
        "Waiting days for quotes or revisions doesn't work when you're on deadline.",
    },
  ],

  testimonial: {
    headline: 'Our event merch used to take weeks. Now it takes minutes.',
    name: 'Marcus Chen',
    title: 'Marketing Director, Briefly',
    image:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
  },

  faqs: [
    {
      question: 'How do you ensure brand consistency?',
      answer:
        'We store your brand guidelines, logos, and color specs. Every mockup and product recommendation is checked against your brand standards before you see it.',
    },
    {
      question: 'Can you help with creative direction?',
      answer:
        'Absolutely. Our team can suggest products, layouts, and print techniques that align with your brand aesthetic. Think of us as an extension of your team.',
    },
    {
      question: 'How fast can you turn around event orders?',
      answer:
        'Standard orders take 2-3 weeks. Rush orders can be done in 5-7 business days. We always confirm timelines upfront so there are no surprises.',
    },
    {
      question: 'What if I need to make changes after approval?',
      answer:
        "We're flexible. Minor changes can often be accommodated even after approval. For bigger changes, we'll give you clear options and updated timelines.",
    },
  ],

  cta: {
    primary: 'Get merch ideas for your next event',
    secondary: 'Get a campaign merch proposal',
    promptTemplate:
      "Hi! I need branded merch for an upcoming event/campaign. I want items that look premium and reinforce our brand identity. Can you suggest some options that would impress?",
  },
}
